import {
  Component,
  inject,
  Renderer2,
  Inject,
  PLATFORM_ID,
  NgZone,
} from '@angular/core';
import { OnInit, ViewChild } from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  type Event,
} from '@angular/router';
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { LoaderService } from '../../services/loader/loader.service';
import { Store } from '@ngrx/store';
import { LayoutState } from '../../../../store/layout/layout-reducers';
import { changesidebarsize } from '../../../../store/layout/layout-action';
import { getSidebarsize } from '../../../../store/layout/layout-selector';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { RightSidebarComponent } from '../right-sidebar/right-sidebar.component';

import { TopbarComponent } from '../topbar/topbar.component';
import { RouterOutlet } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { CommonModule } from '@angular/common';
import { isPlatformBrowser } from '@angular/common';
import {
  NgProgressComponent,
  NgProgressModule,
  type NgProgressRef,
} from 'ngx-progressbar';
import { GlobalService } from '../../services/common/global.service';
import { MENU_ITEMS } from '../../common/menu-meta';
import { ToasterMessageService } from '../../services/topbar-toaster/toaster-message.service';
import { TopBar2Component } from '../top-bar-2/top-bar-2.component';
import { Subscription } from 'rxjs';
import { CareDashboardService } from '../../../care-manager-portal/components/ccm-dashboard/service/care-dashboard.service';
import { TwilioService } from '../../../patient-on-boarding-portal/service/twilio.service';
import { TwilioTokenKeeperService } from '../../../patient-on-boarding-portal/service/twilio-token-keeper.service';
import { session } from '../../common/session/session';
@Component({
  selector: 'app-main-layout',
  imports: [
    SidebarComponent,
    TopbarComponent,
    RouterOutlet,
    CommonModule,
    NgProgressModule,
    TopBar2Component,
  ],
  templateUrl: './main-layout.component.html',
  styleUrl: './main-layout.component.scss',
})
export class MainLayoutComponent {
  private subscription: Subscription = new Subscription();
  private deviceCheckInterval: any;
  private lastDeviceRegisteredState: string | null = null;
  title = 'CaregenixV2';
  authService = inject(AuthService);
  isAuthenticated: boolean = false;
  hideLayout: boolean = false;

  private offcanvasService = inject(NgbOffcanvas);
  private store = inject(Store);
  private renderer = inject(Renderer2);
  private platformId = inject(PLATFORM_ID);
  private loadingService = inject(LoaderService);
  globalService = inject(GlobalService);
  // Cache the platform check
  private isBrowser: boolean = isPlatformBrowser(this.platformId);

  progressRef!: NgProgressRef;
  @ViewChild(NgProgressComponent) progressBar!: NgProgressComponent;
  currentMessage: string = '';
  currentClass: string = '';
  messageTextClass: string = '';
  isVisible: boolean = true;
  icon: string = '';
  path: string = '';
  reload: boolean = false;
  private messageService = inject(ToasterMessageService);
  private router = inject(Router);
  private ngZone = inject(NgZone);
  private twilioTokenKeeper = inject(TwilioTokenKeeperService);
  constructor(
    private careDashboardService: CareDashboardService,
    private twilio: TwilioService,
  ) {
    this.router.events.subscribe((event: Event) => {
      this.checkRouteChange(event);
    });
  }

  onOpenOnboardingOverview(event: { title: string; count: string; tab: string }) {
    console.log('Received event from TopBar2Component:', event);
    this.router.navigate(['/dashboard/doctor/doctor-dashboard/onboarding']);
  }

  checkRouteChange(routerEvent: Event) {
    if (routerEvent instanceof NavigationStart) {
      this.progressBar?.start();
    }
    if (
      routerEvent instanceof NavigationEnd ||
      routerEvent instanceof NavigationCancel ||
      routerEvent instanceof NavigationError
    ) {
      setTimeout(() => {
        this.progressBar?.complete();
      }, 200);
    }

    if (
      routerEvent instanceof NavigationEnd &&
      routerEvent.urlAfterRedirects === '/dashboard' &&
      this.globalService.getUserRole() === 'doctor'
    ) {
      this.router.navigate(['/dashboard/doctor/doctor-dashboard']);
    }
  }

  hideUI: boolean = false;

  ngOnInit(): void {
    this.loadingService.showSpinner();
    this.isAuthenticated = !!this.authService.session;

    // Hide sidebar/topbar if only one group
    const userSettings = JSON.parse(this.globalService.getUserSettings());

    const currentRoute = this.router.url;
    const userRole = localStorage.getItem('userRole');

    if (userRole === 'doctor') this.hideUI = true;

    if (userRole === 'doctor' && currentRoute === '/dashboard') {
      this.router.navigate(['/dashboard/doctor/doctor-dashboard']);
      return;
    }

    if (userSettings.userGroups && userSettings.userGroups.length === 1) {
      this.hideLayout = true;
    }
    if (this.isBrowser) {
      this.store.select('layout').subscribe((data: LayoutState) => {
        document.documentElement.setAttribute(
          'data-bs-theme',
          data.LAYOUT_THEME,
        );
        document.documentElement.setAttribute(
          'data-menu-color',
          data.MENU_COLOR,
        );
        document.documentElement.setAttribute(
          'data-topbar-color',
          data.TOPBAR_COLOR,
        );
        document.documentElement.setAttribute(
          'data-sidenav-size',
          data.MENU_SIZE,
        );
      });

      if (document.documentElement.clientWidth <= 1140) {
        this.onResize();
      }
    }

    if (window.innerWidth > 768) {
      this.router.events.subscribe((event: Event) => {
        if (event instanceof NavigationEnd) {
          if (
            event.urlAfterRedirects.includes('caremanager/dashboard') ||
            event.urlAfterRedirects.includes('dashboard/patientonboarding') ||
            event.urlAfterRedirects.includes(
              'dashboard/ai/ai-Services-dashboard',
            ) ||
            event.urlAfterRedirects.includes('dashboard/sdoh/screening-by-call') ||
            event.urlAfterRedirects.includes('populationhealth/population-health') ||
            event.urlAfterRedirects.includes('analysis/analysis-dashboard')
          ) {
            this.store.dispatch(changesidebarsize({ size: 'condensed' }));
          } else {
            // Set sidebar size based on window width
            if (this.isBrowser && window.innerWidth < 768) {
              this.store.dispatch(changesidebarsize({ size: 'full' }));
            } else {
              this.store.dispatch(changesidebarsize({ size: 'default' }));
            }
          }
        }
      });

      // Set sidebar size immediately on load based on current URL
      const currentUrl = this.router.url;
      if (
        currentUrl.includes('caremanager/dashboard') ||
        currentUrl.includes('dashboard/patientonboarding') ||
        currentUrl.includes('dashboard/ai/ai-Services-dashboard') ||
        currentUrl.includes('dashboard/sdoh/screening-by-call') ||
        currentUrl.includes('populationhealth/population-health') ||
        currentUrl.includes('analysis/analysis-dashboard')
      ) {
        this.store.dispatch(changesidebarsize({ size: 'condensed' }));
      } else {
        if (this.isBrowser && window.innerWidth < 768) {
          this.store.dispatch(changesidebarsize({ size: 'full' }));
        } else {
          this.store.dispatch(changesidebarsize({ size: 'default' }));
        }
      }
    }

    this.loadingService.stopSpinner();

    const userType = this.globalService.getUserRole();
    const userSetting = JSON.parse(this.globalService.getUserSettings());

    const menuItems = MENU_ITEMS.filter(
      (menuItem) =>
        menuItem.rolekey === userType &&
        userSetting.userPermissions.some(
          (permission: any) => menuItem.moduleName === permission.module_name,
        ),
    )
      .map((menuItem) => {
        if (!menuItem.children || menuItem.children.length === 0) {
          // Find the matching permission and screen
          let matchedScreenId = null;
          userSetting.userPermissions.forEach((permission: any) => {
            permission.screens.forEach((screen: any) => {
              if (
                menuItem.screenName === screen.screen_name &&
                screen.is_assigned
              ) {
                matchedScreenId = screen.screen_id;
              }
            });
          });
          const hasPermission = matchedScreenId !== null;
          // Only return the menuItem if permission exists, and add screenId
          return hasPermission
            ? { ...menuItem, screenId: matchedScreenId }
            : null;
        } else if (menuItem.children?.length) {
          const filteredChildren = menuItem.children
            .map((child) => {
              let matchedScreenId = null;
              userSetting.userPermissions.forEach((permission: any) => {
                permission.screens.forEach((screen: any) => {
                  if (
                    child.screenName === screen.screen_name &&
                    screen.is_assigned
                  ) {
                    matchedScreenId = screen.screen_id;
                  }
                });
              });
              return matchedScreenId !== null
                ? { ...child, screenId: matchedScreenId }
                : null;
            })
            .filter((child) => child !== null);
          return filteredChildren.length > 0
            ? { ...menuItem, children: filteredChildren }
            : null;
        }
        return null; // fallback safety
      })
      .filter((menuItem) => menuItem !== null);

    if (menuItems.length == 1) {
      // Find the first menu item with a valid route
      let firstMenu = menuItems[0];
      // If it has children, use the first child

      // Only navigate if not already on the first menu's route
      if (firstMenu.url && this.router.url !== firstMenu.url) {
        if (!localStorage.getItem(session.patientIdFollowUp))
          this.router.navigate([firstMenu.url]);
      }
    }

    if (this.router.url === '/dashboard') {
      const userRole = this.globalService.getUserRole();

      // If the user is patientOnBoarding / care / analysis → route to first menu item
      if (userRole === 'patientOnBoarding' || userRole === 'care' || userRole === 'analysis') {
        const firstMenu = menuItems[0];

        if (firstMenu) {
          // If parent has children → open first child
          if (firstMenu.children && firstMenu.children.length > 0) {
            localStorage.setItem('active-screen-id', firstMenu?.screenId);
            if (!localStorage.getItem(session.patientIdFollowUp))
              this.router.navigate([firstMenu.children[0].url]);
          } else {
            // No children → open the first menu item directly
            localStorage.setItem('active-screen-id', firstMenu?.screenId);
            if (!localStorage.getItem(session.patientIdFollowUp))
              this.router.navigate([firstMenu.url]);
          }
        }
      }
    }

    this.messageService.currentMessage.subscribe(
      ({
        message,
        class: className,
        textClass,
        icon,
        time,
        isVisible,
        path,
        reload
      }) => {
        this.currentMessage = message;
        this.currentClass = className;
        this.messageTextClass = textClass;
        this.isVisible = isVisible;
        this.icon = icon;
        this.path = path;
        this.reload = reload;
        if (this.isVisible) {
          if (time == 0) {
            this.isVisible = true;
            return;
          }
          setTimeout(() => {
            this.isVisible = false;
          }, time);
        }
      },
    );

    // Initial device registration
    this.registerDevice();

    // Start monitoring deviceRegistered status
    this.startDeviceMonitoring();

    // Refresh the Twilio token before it expires on long-lived pages
    this.twilioTokenKeeper.start();
  }
  handlerGo() {
    if (this.path) {
      localStorage.setItem(session.openCriticalReading, 'true');
     if (this.router.url === this.path) {
       this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
         this.router.navigate([this.path]);
       });
     } else {
       this.router.navigate([this.path]);
     }
      this.close()
    }
  }
  handlerReload(){
    window.location.reload();
  }
  private registerDevice(): void {
    this.subscription.add(
      this.careDashboardService.getToken().subscribe((token) => {
        if (token) {
          this.twilio.initialize(token.token);
          localStorage.setItem('deviceRegistered', 'true');
        } else localStorage.setItem('deviceRegistered', 'false');
      }),
    );
  }

  private startDeviceMonitoring(): void {
    // Initialize the last state
    this.lastDeviceRegisteredState = localStorage.getItem('deviceRegistered');

    // Runs outside Angular's zone so this 1s poll doesn't trigger a full
    // app-wide change detection tick every second (steals main-thread time
    // from the WebRTC audio pipeline during an active call, causing jitter).
    this.ngZone.runOutsideAngular(() => {
      this.deviceCheckInterval = setInterval(() => {
        const currentState = localStorage.getItem('deviceRegistered');

        // If state changed to 'false', re-register the device — but never while
        // a call is active, to avoid standing up a second competing Device/
        // PeerConnection mid-call.
        if (
          this.lastDeviceRegisteredState !== 'false' &&
          currentState === 'false' &&
          !this.twilio.getActiveCall()
        ) {
          this.registerDevice();
        }

        this.lastDeviceRegisteredState = currentState;
      }, 1000);
    });
  }

  ngOnDestroy(): void {
    if (this.deviceCheckInterval) {
      clearInterval(this.deviceCheckInterval);
    }
    this.twilioTokenKeeper.stop();
    this.subscription.unsubscribe();
  }

  close() {
    this.isVisible = false;
  }

  onResize() {
    if (this.isBrowser) {
      if (
        document.documentElement.clientWidth <= 1140 &&
        document.documentElement.clientWidth >= 768
      ) {
        this.store.dispatch(changesidebarsize({ size: 'condensed' }));
      } else if (document.documentElement.clientWidth <= 768) {
        this.store.dispatch(changesidebarsize({ size: 'full' }));
      } else {
        this.store.dispatch(changesidebarsize({ size: 'default' }));
        document.documentElement.classList.remove('sidebar-enable');
        const backdrop = document.querySelector('.offcanvas-backdrop');
        if (backdrop) this.renderer.removeChild(document.body, backdrop);
      }

      this.store.select(getSidebarsize).subscribe((size: string) => {
        this.renderer.setAttribute(
          document.documentElement,
          'data-sidenav-size',
          size,
        );
      });
    }
  }

  onSettingsButtonClicked() {
    if (this.isBrowser) {
      this.offcanvasService.open(RightSidebarComponent, {
        position: 'end',
        backdrop: true,
      });
    }
  }

  onToggleMobileMenu() {
    if (this.isBrowser) {
      this.store.select(getSidebarsize).subscribe((size: any) => {
        document.documentElement.setAttribute('data-sidenav-size', size);
      });

      const size = document.documentElement.getAttribute('data-sidenav-size');
      document.documentElement.classList.toggle('sidebar-enable');

      if (size !== 'full') {
        if (document.documentElement.classList.contains('sidebar-enable')) {
          this.store.dispatch(changesidebarsize({ size: 'condensed' }));
        } else {
          this.store.dispatch(changesidebarsize({ size: 'default' }));
        }
      } else {
        this.showBackdrop();
      }
    }
  }

  showBackdrop() {
    if (this.isBrowser) {
      const backdrop = this.renderer.createElement('div');
      this.renderer.addClass(backdrop, 'offcanvas-backdrop');
      this.renderer.addClass(backdrop, 'fade');
      this.renderer.addClass(backdrop, 'show');
      this.renderer.appendChild(document.body, backdrop);
      this.renderer.setStyle(document.body, 'overflow', 'hidden');

      if (window.innerWidth > 1040) {
        this.renderer.setStyle(document.body, 'paddingRight', '15px');
      }

      // Remove sidebar close logic from backdrop click
      this.renderer.listen(backdrop, 'click', () => {
        this.renderer.removeChild(document.body, backdrop);
        this.renderer.setStyle(document.body, 'overflow', null);
        this.renderer.setStyle(document.body, 'paddingRight', null);
      });
    }
  }
}
